import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { cfoNavSectionsForProfile, resolveCfoPageFromNavId, type CfoNavProfile } from './cfoNav'
import {
  buildCfoRoutePath,
  buildFirmCockpitPath,
  cfoFirmOriginSearch,
  cfoPageFromRoute,
  parseCfoRouteState,
} from './cfoRoutes'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { isOpenEndedAskEnabled } from '../../../ai/openEndedAsk'
import { useCfoFirmNavigationOptional } from './CfoFirmNavigation'
import './CfoPageShell.css'

// Canonical deep-link path for a rail item, derived from the current CFO Core
// route (`/{site}/workbooks/{id}/cfo/{page}`). Rendering the rail items as real
// `<a href>` anchors (not bare buttons) is what restores the browser's native
// "Open Link in New Tab / New Window" right-click menu — plus middle-click and
// ⌘/Ctrl-click. Returns `undefined` when the surface isn't mounted under a
// path-based CFO route (e.g. the legacy query-param embed), so the rail falls
// back to plain buttons and behaves exactly as before.
const cfoNavItemHref = (
  navId: string,
  selectedCompany?: { site_slug?: string | null; workbook_id: string } | null,
): string | undefined => {
  if (typeof window === 'undefined') return undefined
  const loc = parseCfoRouteState(window.location)
  const siteSlug = loc?.siteSlug || String(selectedCompany?.site_slug || '').trim()
  const workbookId = loc?.workbookId || String(selectedCompany?.workbook_id || '').trim()
  if (!siteSlug || !workbookId) return undefined
  const route = resolveCfoPageFromNavId(navId)
  if (!route) return undefined
  const path = buildCfoRoutePath({
    siteSlug,
    workbookId,
    page: cfoPageFromRoute(route),
  })
  const firmContextSearch = cfoFirmOriginSearch(window.location)
    || (!loc && selectedCompany ? '?fg_origin=firm' : '')
  return `${path}${firmContextSearch}`
}

// CfoPageShell — the single 3-zone app frame for the unified CFO Core surface
// (nav rail · main · copilot rail). Extracted from `CfoHomeBrief.tsx` so the
// Brief and every interior page share ONE frame: the calm linked-page theme
// (petrol/teal/clay/amber) ported from `docs/mockups/cfo-linked/_cfo-shell.css`.
// Styled by `CfoPageShell.css`.
//
// One component, one frame: drift is impossible by construction.

export type CfoShellCacheTone = 'idle' | 'ready' | 'failed'

export type CfoShellCacheState = {
  tone: CfoShellCacheTone
  label: string
  title?: string
}

export type CfoShellSelectOption = {
  id: string
  label: string
  disabled?: boolean
  hint?: string
}

const CfoShellActionsContext = createContext<ReactNode>(null)
const CfoNavAvailabilityContext = createContext({ jobCosting: false })

export function CfoNavAvailabilityProvider({
  jobCosting,
  children,
}: {
  jobCosting: boolean
  children: ReactNode
}) {
  const value = useMemo(() => ({ jobCosting }), [jobCosting])
  return (
    <CfoNavAvailabilityContext.Provider value={value}>
      {children}
    </CfoNavAvailabilityContext.Provider>
  )
}

export function CfoShellActionsProvider({
  actions = null,
  children,
}: {
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <CfoShellActionsContext.Provider value={actions}>
      {children}
    </CfoShellActionsContext.Provider>
  )
}

function useCfoShellActions() {
  return useContext(CfoShellActionsContext)
}

export type CfoShellSelectChipProps = {
  label: string
  options: CfoShellSelectOption[]
  value?: string | null
  onChange?: (value: string) => void
  ariaLabel: string
  className?: string
  menuAlign?: 'start' | 'end'
}

export function CfoShellSelectChip({
  label,
  options,
  value,
  onChange,
  ariaLabel,
  className = '',
  menuAlign = 'start',
}: CfoShellSelectChipProps) {
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const selectedValue = value || options[0]?.id || ''
  const selectedOption = useMemo(
    () => options.find((option) => option.id === selectedValue) || options[0] || null,
    [options, selectedValue],
  )
  const displayLabel = selectedOption?.label || label
  const disabled = !options.length || !onChange

  useEffect(() => {
    if (!open) return
    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocumentClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (disabled) return <span className={`cfo-shell-chip ${className}`.trim()}>{label}</span>

  return (
    <div
      className={`cfo-shell-chip cfo-shell-select-chip cfo-shell-menu-chip ${className}`.trim()}
      ref={rootRef}
      data-open={open ? 'true' : undefined}
      data-menu-align={menuAlign}
      onClick={() => setOpen((value) => !value)}
    >
      <select
        className="cfo-shell-hidden-select"
        aria-label={ariaLabel}
        tabIndex={-1}
        value={selectedValue}
        onChange={(event) => onChange?.(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="cfo-shell-select-button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
      >
        <span>{displayLabel}</span>
      </button>
      {open ? (
        <div className="cfo-shell-select-menu" id={menuId} role="listbox" aria-label={ariaLabel}>
          {options.map((option) => {
            const selected = option.id === selectedValue
            return (
              <button
                type="button"
                role="option"
                aria-selected={selected}
                aria-disabled={option.disabled ? true : undefined}
                title={option.hint || undefined}
                className={selected ? 'selected' : undefined}
                key={option.id}
                disabled={option.disabled}
                onClick={(event) => {
                  event.stopPropagation()
                  if (option.disabled) return
                  onChange?.(option.id)
                  setOpen(false)
                }}
              >
                <span className="cfo-shell-select-check" aria-hidden="true" />
                <span>{option.label}</span>
                {option.disabled && option.hint ? <small>{option.hint}</small> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export type CfoPageShellProps = {
  /** Active nav item id (e.g. `home`, `financials`). Drives the rail highlight. */
  activeNavId: string
  onNavSelect: (navId: string) => void
  /** Brand block (rail header). */
  brandLabel?: string
  brandSub?: string
  /** Topbar breadcrumb (left). */
  crumb: ReactNode
  /** Period chip text (e.g. "June 2026"). */
  asOfLabel: string
  periodOptions?: CfoShellSelectOption[]
  periodValue?: string
  onPeriodChange?: (value: string) => void
  periodAriaLabel?: string
  /** Comparison chip text (e.g. "vs. prior month"). */
  comparisonLabel?: string
  comparisonOptions?: CfoShellSelectOption[]
  comparisonValue?: string
  onComparisonChange?: (value: string) => void
  comparisonAriaLabel?: string
  /** Optional cache pill. */
  cacheState?: CfoShellCacheState | null
  /** Focuses / opens the copilot when the topbar "Ask" affordance is used. */
  onAsk?: () => void
  askPlaceholder?: string
  /** Copilot rail content (slot). */
  copilot?: ReactNode
  /** Optional actions rendered at the right side of the topbar. */
  topbarActions?: ReactNode
  /** Some full-frame modules provide their own fixed header and do not need the
   * generic breadcrumb strip. */
  showTopbar?: boolean
  /** Main content (interior page or Brief body). */
  children: ReactNode
  /** Overlay siblings rendered inside the frame after the copilot (e.g. JourneyShell). */
  overlay?: ReactNode
  /** Applied to the frame element for telemetry parity (`data-surface`). */
  dataSurface?: string
  /** Full legacy rail or launch-lightweight rail. */
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

type ShellClassNames = {
  frame: string
  rail: string
  railCo: string
  railLogo: string
  railMeta: string
  railGroup: string
  navsec: string
  nav: string
  navSub: string
  navActive: string
  navIc: string
  main: string
  topbar: string
  crumb: string
  period: string
  cache: string
  cacheReady: string
  cacheFailed: string
  cacheDot: string
  ask: string
  askSpark: string
  content: string
  copilot: string
}

const LINKED_CLASSES: ShellClassNames = {
  frame: 'cfo-shell',
  rail: 'cfo-shell-nav',
  railCo: 'cfo-shell-brand',
  railLogo: 'cfo-shell-mark',
  railMeta: 'cfo-shell-brand-meta',
  railGroup: 'cfo-shell-nav-group',
  navsec: 'cfo-shell-nav-label',
  nav: 'cfo-shell-nav-item',
  navSub: 'sub',
  navActive: 'active',
  navIc: 'cfo-shell-nav-ic',
  main: 'cfo-shell-main',
  topbar: 'cfo-shell-topbar',
  crumb: 'cfo-shell-crumb',
  period: 'cfo-shell-chip',
  cache: 'cfo-shell-cache',
  cacheReady: 'ready',
  cacheFailed: 'failed',
  cacheDot: 'cfo-shell-cache-dot',
  ask: 'cfo-shell-ask',
  askSpark: 'cfo-shell-ask-spark',
  content: 'cfo-shell-content',
  copilot: 'cfo-shell-rail',
}

type CfoDuoIconName =
  | 'ceo'
  | 'home'
  | 'financials'
  | 'ratios'
  | 'cash'
  | 'ar'
  | 'forecast'
  | 'scenarios'
  | 'valuation'
  | 'presentations'
  | 'explore'
  | 'layout'
  | 'adjustments'
  | 'gl_detail'
  | 'spark'
  | 'trend'

const NAV_ICON_BY_ID: Record<string, CfoDuoIconName> = {
  ceo: 'ceo',
  home: 'home',
  financials: 'financials',
  ratios: 'ratios',
  cash: 'cash',
  ar: 'ar',
  forecast: 'forecast',
  scenarios: 'scenarios',
  valuation: 'valuation',
  presentations: 'presentations',
  explore: 'explore',
  layout: 'layout',
  adjustments: 'adjustments',
  gl_detail: 'gl_detail',
}

function CfoDuoIcon({ name, className = 'ic' }: { name: CfoDuoIconName; className?: string }) {
  switch (name) {
    case 'ceo':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M4 16.5c2.2-3.2 4.4-4.8 8-4.8s5.8 1.6 8 4.8H4Z" />
          <circle className="duo-stroke" cx="12" cy="8" r="2.4" />
          <path className="duo-stroke" d="M12 3.5v1.6M7.2 5.6l1.1 1.1M16.8 5.6l-1.1 1.1M4.8 9.2h1.7M17.5 9.2h1.7" />
          <path className="duo-stroke" d="M4 16.5c2.2-3.2 4.4-4.8 8-4.8s5.8 1.6 8 4.8" />
        </svg>
      )
    case 'home':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M12 3.4 4 9.6V20a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.6L12 3.4Z" />
          <path className="duo-stroke" d="M4 9.6 12 3.4l8 6.2V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.6Z" />
          <path className="duo-stroke" d="M9.5 21v-5a2.5 2.5 0 0 1 5 0v5" />
        </svg>
      )
    case 'financials':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4Z" />
          <path className="duo-stroke" d="M5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4Z" />
          <path className="duo-stroke" d="M8 16.5v-3M12 16.5v-6M16 16.5v-8" />
        </svg>
      )
    case 'ratios':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M4 18 9 12l4 3 7-9v12H4Z" />
          <path className="duo-stroke" d="M4 16 9 11l4 3 7-9" />
          <circle className="duo-stroke" cx="20" cy="5" r="1.4" />
        </svg>
      )
    case 'cash':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M3.5 7h17a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-17a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
          <path className="duo-stroke" d="M3.5 7h17a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-17a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
          <circle className="duo-stroke" cx="12" cy="12" r="2.4" />
          <path className="duo-stroke" d="M5.5 9.5v5M18.5 9.5v5" />
        </svg>
      )
    case 'ar':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M6 3.5h12a1 1 0 0 1 1 1V20l-2.3-1.4L14.4 20 12 18.6 9.6 20 7.3 18.6 5 20V4.5a1 1 0 0 1 1-1Z" />
          <path className="duo-stroke" d="M6 3.5h12a1 1 0 0 1 1 1V20l-2.3-1.4L14.4 20 12 18.6 9.6 20 7.3 18.6 5 20V4.5a1 1 0 0 1 1-1Z" />
          <path className="duo-stroke" d="M8.5 8.5h7M8.5 12h5" />
        </svg>
      )
    case 'forecast':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="duo-fill" cx="12" cy="12" r="8" />
          <circle className="duo-stroke" cx="12" cy="12" r="8" />
          <path className="duo-stroke" d="M12 8.5v7M8.5 12h7" />
        </svg>
      )
    case 'scenarios':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="duo-fill" cx="6.5" cy="6.5" r="2.2" />
          <circle className="duo-fill" cx="17.5" cy="9" r="2.2" />
          <circle className="duo-stroke" cx="6.5" cy="6.5" r="2.2" />
          <circle className="duo-stroke" cx="17.5" cy="9" r="2.2" />
          <circle className="duo-stroke" cx="6.5" cy="17.5" r="2.2" />
          <path className="duo-stroke" d="M6.5 8.7v6.6M6.5 14.5h6a3 3 0 0 0 3-3v-.3" />
        </svg>
      )
    case 'valuation':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="m12 4 7 6-7 10-7-10Z" />
          <path className="duo-stroke" d="m12 4 7 6-7 10-7-10Z" />
          <path className="duo-stroke" d="M5 10h14M9.5 4 8 10l4 10M14.5 4 16 10l-4 10" />
        </svg>
      )
    case 'presentations':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
          <path className="duo-stroke" d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
          <path className="duo-stroke" d="M8.5 13v-2M12 13V9.5M15.5 13v-3M12 16v3M9 21h6" />
        </svg>
      )
    case 'explore':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M4.5 4.5h6v6h-6z" />
          <rect className="duo-stroke" x="4.5" y="4.5" width="15" height="15" rx="1.5" />
          <path className="duo-stroke" d="M12 4.5v15M4.5 12h15" />
        </svg>
      )
    case 'layout':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M4.5 5h5v14h-5z" />
          <rect className="duo-stroke" x="4.5" y="5" width="15" height="14" rx="1.5" />
          <path className="duo-stroke" d="M9.5 5v14" />
        </svg>
      )
    case 'adjustments':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="duo-fill" cx="9" cy="8" r="2.2" />
          <circle className="duo-fill" cx="15" cy="16" r="2.2" />
          <path className="duo-stroke" d="M4.5 8h13M4.5 16h13" />
          <circle className="duo-stroke" cx="9" cy="8" r="2.2" />
          <circle className="duo-stroke" cx="15" cy="16" r="2.2" />
        </svg>
      )
    case 'gl_detail':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M5 5h14v14H5z" />
          <path className="duo-stroke" d="M5.5 7h13M5.5 12h13M5.5 17h8" />
        </svg>
      )
    case 'spark':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-fill" d="M12 3.5 13.4 9 19 10.4 13.4 11.8 12 17.5 10.6 11.8 5 10.4 10.6 9Z" />
          <path className="duo-stroke" d="M12 3.5 13.4 9 19 10.4 13.4 11.8 12 17.5 10.6 11.8 5 10.4 10.6 9Z" />
        </svg>
      )
    case 'trend':
    default:
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path className="duo-stroke" d="M4 16 9 10l3.5 3L20 5" />
          <circle cx="20" cy="5" r="2" fill="currentColor" />
        </svg>
      )
  }
}

// Derive the current site's home URL (`/{slug}/workbooks/`) from the active CFO
// route and navigate back to it in-app. Wired to the rail brand mark so the
// FuseGrid logo doubles as a "return to site home" affordance. Uses the app's
// canonical client navigation (pushState + `fg_route_change`) so `App` swaps
// from `cfo_core` to the admin/site-home surface without a full reload.
export const navigateToSiteHome = () => {
  if (typeof window === 'undefined') return
  const parts = window.location.pathname.split('/').map((part) => part.trim()).filter(Boolean)
  const siteSlug = parts[1] === 'workbooks' ? decodeURIComponent(parts[0] || '') : ''
  const target = siteSlug ? `/${encodeURIComponent(siteSlug)}/workbooks/` : '/'
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (target === current) return
  window.history.pushState(window.history.state, '', target)
  window.dispatchEvent(new Event('fg_route_change'))
}

const firmCockpitReturnPath = (): string | null => {
  if (typeof window === 'undefined' || !cfoFirmOriginSearch(window.location)) return null
  const route = parseCfoRouteState(window.location)
  return route ? buildFirmCockpitPath(route.siteSlug) : null
}

const navigateToFirmCockpit = (targetOverride?: string | null) => {
  const target = targetOverride || firmCockpitReturnPath()
  if (!target || typeof window === 'undefined') return
  window.history.pushState(window.history.state, '', target)
  window.dispatchEvent(new Event('fg_route_change'))
}

export function CfoPageShell({
  activeNavId,
  onNavSelect,
  brandLabel = 'CFO Core',
  brandSub = '',
  crumb,
  asOfLabel,
  periodOptions = [],
  periodValue,
  onPeriodChange,
  periodAriaLabel = 'Select financial period',
  comparisonLabel = 'vs. prior month',
  comparisonOptions = [],
  comparisonValue,
  onComparisonChange,
  comparisonAriaLabel = 'Select comparison',
  cacheState = null,
  onAsk,
  askPlaceholder = 'Ask anything about your finances…',
  copilot,
  topbarActions,
  showTopbar = true,
  children,
  overlay,
  dataSurface,
  navProfile = 'full',
  runtimeProfile,
}: CfoPageShellProps) {
  const c = LINKED_CLASSES
  const firmNavigation = useCfoFirmNavigationOptional()
  const navAvailability = useContext(CfoNavAvailabilityContext)
  const jobCostingAvailable = navAvailability.jobCosting
    || firmNavigation?.selectedClient?.capabilities?.job_costing === true
  const navSections = useMemo(
    () => cfoNavSectionsForProfile(navProfile, { jobCosting: jobCostingAvailable }),
    [jobCostingAvailable, navProfile],
  )
  const contextActions = useCfoShellActions()
  const shellActions = topbarActions ?? contextActions
  const frameClass = `${c.frame}${runtimeProfile?.mode === 'mobile-constrained' ? ' cfo-runtime-mobile' : ''}`
  const firmReturnPath = firmNavigation?.hasMultipleCompanies
    ? buildFirmCockpitPath(firmNavigation.siteSlug)
    : firmNavigation
      ? null
      : firmCockpitReturnPath()

  const cacheClass = cacheState
    ? `${c.cache}${cacheState.tone === 'ready' ? ` ${c.cacheReady}` : ''}${
        cacheState.tone === 'failed' ? ` ${c.cacheFailed}` : ''
      }`
    : ''

  const renderChipSelect = (
    label: string,
    options: CfoShellSelectOption[],
    value: string | undefined,
    onChange: ((value: string) => void) | undefined,
    ariaLabel: string,
  ) => {
    if (!label && !options.length) return null
    return <CfoShellSelectChip label={label} options={options} value={value} onChange={onChange} ariaLabel={ariaLabel} />
  }

  return (
    <div
      className={frameClass}
      data-surface={dataSurface}
      data-runtime-mode={runtimeProfile?.mode}
      data-no-rail={copilot ? undefined : 'true'}
    >
      <nav className={c.rail} aria-label="CFO Core sections">
        <button
          type="button"
          className={c.railCo}
          onClick={navigateToSiteHome}
          title="Return to site home"
          aria-label="Return to site home"
        >
          <div className={c.railLogo} aria-hidden="true">
            <CfoDuoIcon name="trend" />
          </div>
          <div className={c.railMeta}>
            <b>{brandLabel}</b>
            <span>{brandSub}</span>
          </div>
        </button>
        {firmReturnPath ? (
          <a
            href={firmReturnPath}
            className={`cfo-shell-firm-return${activeNavId === 'firm' ? ' active' : ''}`}
            onClick={(event) => {
              if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              ) return
              event.preventDefault()
              navigateToFirmCockpit(firmReturnPath)
            }}
            aria-current={activeNavId === 'firm' ? 'page' : undefined}
            aria-label="Firm cockpit"
          >
            <span className={c.navIc} aria-hidden="true"><CfoDuoIcon name="trend" /></span>
            <span>Firm cockpit</span>
          </a>
        ) : null}
        {navSections.map((section) => (
          <div className={c.railGroup} key={section.id}>
            {section.label ? <div className={c.navsec}>{section.label}</div> : null}
            {section.items.map((item) => {
              const active = item.id === activeNavId
              const icon = NAV_ICON_BY_ID[item.id] || 'explore'
              const className = `${c.nav}${item.sub ? ` ${c.navSub}` : ''}${active ? ` ${c.navActive}` : ''}`
              const inner = (
                <>
                  <span className={c.navIc} aria-hidden="true">
                    <CfoDuoIcon name={icon} />
                  </span>
                  <span>{item.label}</span>
                </>
              )
              const href = cfoNavItemHref(item.id, firmNavigation?.selectedClient)
              // Render a real link so right-click → "Open in New Tab/Window",
              // middle-click, and ⌘/Ctrl-click all work. A plain left click is
              // still handled in-app (no full reload); modifier / non-primary
              // clicks fall through to the browser's default link handling.
              if (href) {
                return (
                  <a
                    key={item.id}
                    href={href}
                    className={className}
                    data-fg-tour={`nav.${item.id}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={(event) => {
                      if (
                        event.defaultPrevented ||
                        event.button !== 0 ||
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                      ) {
                        return
                      }
                      event.preventDefault()
                      onNavSelect(item.id)
                    }}
                  >
                    {inner}
                  </a>
                )
              }
              return (
                <button
                  type="button"
                  key={item.id}
                  className={className}
                  data-fg-tour={`nav.${item.id}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => onNavSelect(item.id)}
                >
                  {inner}
                </button>
              )
            })}
          </div>
        ))}
      </nav>

      <main className={c.main} role="main">
        {showTopbar ? (
          <div className={c.topbar}>
            <div className={c.crumb}>{crumb}</div>
            {renderChipSelect(asOfLabel, periodOptions, periodValue, onPeriodChange, periodAriaLabel)}
            {renderChipSelect(comparisonLabel, comparisonOptions, comparisonValue, onComparisonChange, comparisonAriaLabel)}
            {cacheState ? (
              <div className={cacheClass} title={cacheState.title} role="status" aria-label={cacheState.label}>
                <span className={c.cacheDot} aria-hidden="true" />
                <span>{cacheState.label}</span>
              </div>
            ) : null}
            {isOpenEndedAskEnabled() ? (
              <button type="button" className={c.ask} onClick={onAsk} aria-label="Open CFO copilot">
                <CfoDuoIcon name="spark" className={`ic ${c.askSpark}`} />
                <span>{askPlaceholder}</span>
                <kbd>⌘K</kbd>
              </button>
            ) : null}
            {shellActions ? <div className="cfo-shell-actions">{shellActions}</div> : null}
          </div>
        ) : null}

        <div className={c.content}>{children}</div>
      </main>

      {copilot ? (
        <aside className={c.copilot} aria-label="CFO Copilot">
          {copilot}
        </aside>
      ) : null}

      {overlay}
    </div>
  )
}

export default CfoPageShell
